import { MentionNodeAttrs } from "@tiptap/extension-mention"
import "./MentionList.scss"

import React, { forwardRef, useEffect, useImperativeHandle, useState } from "react"
import { i18n } from "@lingui/core"
import { classSet } from "@fider/services"

// Tùy biến KHCN: một người trong danh sách gợi ý @ (tác giả bài hoặc người đã bình luận)
export interface MentionItem extends MentionNodeAttrs {
  avatarURL?: string
  organization?: string
}

interface Props {
  items: MentionItem[]
  command?: (item: MentionNodeAttrs) => void
}

export interface MentionListHandle {
  onKeyDown: (args: { event: KeyboardEvent }) => boolean
}

const initialOf = (name: string) => (name.trim().split(/\s+/).pop() || name).charAt(0).toUpperCase()

const MentionList = forwardRef<MentionListHandle, Props>((props, ref) => {
  const [selectedIndex, setSelectedIndex] = useState(0)

  const selectItem = (index: number) => {
    const item = props.items?.[index]

    if (item) {
      props.command?.({ id: item.id, label: item.label })
    }
  }
  const upHandler = () => {
    setSelectedIndex((selectedIndex + props.items.length - 1) % props.items.length)
  }

  const downHandler = () => {
    setSelectedIndex((selectedIndex + 1) % props.items.length)
  }

  const enterHandler = () => {
    selectItem(selectedIndex)
  }

  useEffect(() => setSelectedIndex(0), [props.items])

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }: { event: KeyboardEvent }): boolean => {
      if (event.key === "ArrowUp") {
        upHandler()
        return true
      }

      if (event.key === "ArrowDown") {
        downHandler()
        return true
      }

      if (event.key === "Enter") {
        enterHandler()
        return true
      }

      return false
    },
  }))

  return (
    <div className="c-mention-list" role="listbox">
      {props.items.length ? (
        props.items.map((item, index) => (
          <button
            type="button"
            role="option"
            aria-selected={index === selectedIndex}
            className={classSet({ "c-mention-list__item": true, "is-selected": index === selectedIndex })}
            key={item.id ?? index}
            onMouseEnter={() => setSelectedIndex(index)}
            onClick={() => selectItem(index)}
          >
            {item.avatarURL ? (
              <img className="c-mention-list__avatar" src={item.avatarURL} alt="" />
            ) : (
              <span className="c-mention-list__initial" aria-hidden="true">
                {initialOf(item.label ?? "")}
              </span>
            )}
            <span className="c-mention-list__text">
              <span className="c-mention-list__name">{item.label}</span>
              {item.organization && <span className="c-mention-list__meta">{item.organization}</span>}
            </span>
          </button>
        ))
      ) : (
        <div className="c-mention-list__empty">{i18n._({ id: "khcn.mention.noresult", message: "No one to mention in this post yet" })}</div>
      )}
    </div>
  )
})

MentionList.displayName = "MentionList"

export default MentionList
